import type {
  LocalCaseRef,
  LocalCaseSearch,
  LocalCaseText,
  LocalDirectCases,
  LocalRelatedCases,
  LocalStatuteSearch,
  LocalTransport,
  VolumeKey,
} from "@beoptong/core";
import type {
  SearchEvent,
  SearchReply,
  SearchRequest,
  VolumeStatus,
} from "./search-protocol";

interface Pending {
  resolve(value: unknown): void;
  reject(error: Error): void;
  timer?: ReturnType<typeof setTimeout>;
}

/**
 * search.worker.ts 위의 얇은 RPC 클라이언트.
 * LocalSearchProvider는 이 클래스의 존재를 모른다(transport 인터페이스만 안다).
 */
export class WorkerSearchTransport implements LocalTransport {
  private readonly pending = new Map<number, Pending>();
  private readonly listeners = new Set<(event: SearchEvent) => void>();
  private sequence = 0;

  constructor(private readonly worker: Worker) {
    worker.addEventListener("message", this.onMessage);
    worker.addEventListener("error", this.onError);
    worker.addEventListener("messageerror", this.onError);
  }

  private readonly onMessage = (event: MessageEvent<SearchReply | SearchEvent>): void => {
    const data = event.data;
    if (!data) return;
    if ("type" in data && data.type === "import-progress") {
      for (const listener of this.listeners) listener(data);
      return;
    }
    const message = data as SearchReply;
    const waiter = this.pending.get(message.id);
    if (!waiter) return;
    this.pending.delete(message.id);
    if (waiter.timer) clearTimeout(waiter.timer);
    if (message.ok) waiter.resolve(message.value);
    else waiter.reject(new Error(message.error));
  };

  private readonly onError = (event: Event): void => {
    const message =
      "message" in event && typeof event.message === "string"
        ? event.message
        : "검색 워커가 중단되었습니다";
    const error = new Error(message);
    for (const waiter of this.pending.values()) {
      if (waiter.timer) clearTimeout(waiter.timer);
      waiter.reject(error);
    }
    this.pending.clear();
  };

  onProgress(listener: (event: SearchEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private send<T>(request: Record<string, unknown> & { type: SearchRequest["type"] }, timeoutMs = 60000): Promise<T> {
    const id = ++this.sequence;
    const message = { ...request, id } as SearchRequest;
    return new Promise<T>((resolve, reject) => {
      const waiter: Pending = {
        resolve: (value) => resolve(value as T),
        reject,
      };
      if (timeoutMs > 0) {
        waiter.timer = setTimeout(() => {
          this.pending.delete(id);
          reject(new Error("로컬 검색 응답 시간이 초과되었습니다"));
        }, timeoutMs);
      }
      this.pending.set(id, waiter);
      this.worker.postMessage(message);
    });
  }

  status(): Promise<VolumeStatus> {
    return this.send<VolumeStatus>({ type: "status" });
  }

  /** 공기격리(air-gap) 볼륨 가져오기. 파일명은 정본 이름으로 고정한다. */
  importVolume(
    volume: "core" | "cases",
    name: string,
    file: File,
  ): Promise<VolumeStatus> {
    return this.send<VolumeStatus>({ type: "import", volume, name, file }, 0);
  }

  /**
   * 다운로드/가져오기 완료 후 검색 워커가 OPFS 원본을 다시 열게 한다.
   * 커널/ tail 빌드 시간이 걸릴 수 있으므로 타임아웃을 두지 않는다.
   */
  reopen(volumes?: VolumeKey[]): Promise<VolumeStatus> {
    return this.send<VolumeStatus>({ type: "reopen", volumes }, 0);
  }

  searchStatutes(query: string, limit: number): Promise<LocalStatuteSearch> {
    return this.send<LocalStatuteSearch>({ type: "searchStatutes", query, limit });
  }

  directCases(law: string, article: string): Promise<LocalDirectCases> {
    return this.send<LocalDirectCases>({ type: "directCases", law, article });
  }

  searchCases(query: string, limit: number): Promise<LocalCaseSearch> {
    return this.send<LocalCaseSearch>({ type: "searchCases", query, limit });
  }

  relatedCases(
    locus: string,
    text: string,
    exclude: readonly string[],
    limit: number,
    includeDirect = false,
  ): Promise<LocalRelatedCases> {
    return this.send<LocalRelatedCases>({
      type: "relatedCases",
      locus,
      text,
      exclude: [...exclude],
      limit,
      includeDirect,
    });
  }

  caseByNumber(caseNo: string, court?: string): Promise<LocalCaseRef | null> {
    return this.send<LocalCaseRef | null>({ type: "caseByNumber", caseNo, court });
  }

  caseText(pointer: string): Promise<LocalCaseText> {
    return this.send<LocalCaseText>({ type: "caseText", pointer });
  }

  /** 검색 한 번에 한 번만 호출된다. 워커의 status()가 전문 볼륨 오픈 상태를 안다. */
  async caseTextReady(): Promise<boolean> {
    const status = await this.status();
    return status.caseTextReady === true;
  }
}
